import React, { useState, useEffect } from 'react';
import { CATEGORIES } from './SparesUsed';
import {
  createSparePart, listCatalog, receiveStock, issueStock, listEntries, listIssues
} from '../services/sparePartsService';
import './SparePartsInventory.css';

function SparePartsInventory({ user, technicians }) {
  const [tab, setTab] = useState('catalog'); // catalog | receive | issue

  const [catalog, setCatalog] = useState([]);
  const [entries, setEntries] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [showAddPart, setShowAddPart] = useState(false);
  const [newPart, setNewPart] = useState({ name: '', code: '', category: 'other', unitCost: '', reorderLevel: '' });

  const [receiveForm, setReceiveForm] = useState({ partId: '', quantity: '', unitCost: '', supplier: '', notes: '' });
  const [issueForm, setIssueForm] = useState({ partId: '', quantity: '', technicianId: '', notes: '' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, e, i] = await Promise.all([listCatalog(), listEntries(), listIssues()]);
      setCatalog(c);
      setEntries(e);
      setIssues(i);
    } catch (err) {
      console.error('Error loading spare parts data:', err);
    } finally {
      setLoading(false);
    }
  };

  const categoryLabel = (value) => CATEGORIES.find(c => c.value === value)?.label || value;

  const handleAddPart = async () => {
    if (!newPart.name.trim()) { setError('Part name is required.'); return; }
    setBusy(true);
    setError('');
    try {
      await createSparePart({ ...newPart, createdBy: user });
      setNewPart({ name: '', code: '', category: 'other', unitCost: '', reorderLevel: '' });
      setShowAddPart(false);
      await loadAll();
    } catch (err) {
      console.error('Error creating part:', err);
      setError('Could not create this part. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleReceive = async () => {
    const part = catalog.find(p => p.id === receiveForm.partId);
    if (!part) { setError('Please select a part.'); return; }
    setBusy(true);
    setError('');
    try {
      await receiveStock({
        sparePart: part,
        quantity: receiveForm.quantity,
        unitCost: receiveForm.unitCost,
        supplier: receiveForm.supplier,
        notes: receiveForm.notes,
        receivedBy: user
      });
      setReceiveForm({ partId: '', quantity: '', unitCost: '', supplier: '', notes: '' });
      await loadAll();
    } catch (err) {
      console.error('Error receiving stock:', err);
      setError(err.message || 'Could not record this delivery. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleIssue = async () => {
    const part = catalog.find(p => p.id === issueForm.partId);
    const technician = technicians.find(t => t.id === issueForm.technicianId);
    if (!part) { setError('Please select a part.'); return; }
    if (!technician) { setError('Please select a technician.'); return; }
    setBusy(true);
    setError('');
    try {
      await issueStock({
        sparePart: part,
        quantity: issueForm.quantity,
        technician,
        notes: issueForm.notes,
        issuedBy: user
      });
      setIssueForm({ partId: '', quantity: '', technicianId: '', notes: '' });
      await loadAll();
    } catch (err) {
      console.error('Error issuing stock:', err);
      setError(err.message || 'Could not issue this part. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return 'Just now';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-KE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const lowStockCount = catalog.filter(p => p.currentStock <= p.reorderLevel).length;

  return (
    <div className="spi">
      <div className="spi-header">
        <h2>📦 Spare Parts Inventory</h2>
        {lowStockCount > 0 && <span className="spi-badge">{lowStockCount} low stock</span>}
      </div>

      <div className="spi-tabs">
        <button className={`spi-tab ${tab === 'catalog' ? 'active' : ''}`} onClick={() => setTab('catalog')}>📋 Catalog</button>
        <button className={`spi-tab ${tab === 'receive' ? 'active' : ''}`} onClick={() => setTab('receive')}>📥 Receive Stock</button>
        <button className={`spi-tab ${tab === 'issue' ? 'active' : ''}`} onClick={() => setTab('issue')}>📤 Issue to Technician</button>
      </div>

      {error && <div className="spi-error">⚠️ {error}</div>}

      {loading ? (
        <div className="spi-empty">Loading inventory...</div>
      ) : (
        <>
          {/* ── CATALOG ── */}
          {tab === 'catalog' && (
            <div>
              <button className="spi-btn spi-btn-primary" onClick={() => setShowAddPart(!showAddPart)}>
                {showAddPart ? 'Cancel' : '+ Add New Part'}
              </button>

              {showAddPart && (
                <div className="spi-form-card">
                  <input className="spi-input" placeholder="Part name" value={newPart.name}
                    onChange={e => setNewPart({ ...newPart, name: e.target.value })} />
                  <input className="spi-input" placeholder="Code / SKU (optional)" value={newPart.code}
                    onChange={e => setNewPart({ ...newPart, code: e.target.value })} />
                  <select className="spi-input" value={newPart.category}
                    onChange={e => setNewPart({ ...newPart, category: e.target.value })}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <input className="spi-input" type="number" placeholder="Reference unit cost" value={newPart.unitCost}
                    onChange={e => setNewPart({ ...newPart, unitCost: e.target.value })} />
                  <input className="spi-input" type="number" placeholder="Reorder level (low-stock alert)" value={newPart.reorderLevel}
                    onChange={e => setNewPart({ ...newPart, reorderLevel: e.target.value })} />
                  <button className="spi-btn spi-btn-primary" onClick={handleAddPart} disabled={busy}>
                    {busy ? 'Saving...' : 'Save Part'}
                  </button>
                </div>
              )}

              <div className="spi-list">
                {catalog.length === 0 ? (
                  <div className="spi-empty">No parts in the catalog yet.</div>
                ) : catalog.map(part => (
                  <div key={part.id} className={`spi-card ${part.currentStock <= part.reorderLevel ? 'spi-low' : ''}`}>
                    <div className="spi-card-top">
                      <span className="spi-part-name">{part.name}</span>
                      <span className="spi-stock">{part.currentStock} in stock</span>
                    </div>
                    <div className="spi-card-meta">
                      {categoryLabel(part.category)} {part.code && `• ${part.code}`} • KES {part.unitCost}/unit
                      {part.currentStock <= part.reorderLevel && <span className="spi-low-tag"> • ⚠️ Low stock</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── RECEIVE STOCK ── */}
          {tab === 'receive' && (
            <div>
              <div className="spi-form-card">
                <select className="spi-input" value={receiveForm.partId}
                  onChange={e => setReceiveForm({ ...receiveForm, partId: e.target.value })}>
                  <option value="">Select part...</option>
                  {catalog.map(p => <option key={p.id} value={p.id}>{p.name} ({p.currentStock} in stock)</option>)}
                </select>
                <input className="spi-input" type="number" placeholder="Quantity received" value={receiveForm.quantity}
                  onChange={e => setReceiveForm({ ...receiveForm, quantity: e.target.value })} />
                <input className="spi-input" type="number" placeholder="Unit cost (optional, uses reference cost if blank)" value={receiveForm.unitCost}
                  onChange={e => setReceiveForm({ ...receiveForm, unitCost: e.target.value })} />
                <input className="spi-input" placeholder="Supplier (optional)" value={receiveForm.supplier}
                  onChange={e => setReceiveForm({ ...receiveForm, supplier: e.target.value })} />
                <textarea className="spi-input" placeholder="Notes (optional)" value={receiveForm.notes} rows={2}
                  onChange={e => setReceiveForm({ ...receiveForm, notes: e.target.value })} />
                <button className="spi-btn spi-btn-primary" onClick={handleReceive} disabled={busy}>
                  {busy ? 'Saving...' : '📥 Record Delivery'}
                </button>
              </div>

              <div className="spi-list">
                {entries.slice(0, 15).map(e => (
                  <div key={e.id} className="spi-card">
                    <div className="spi-card-top">
                      <span className="spi-part-name">+{e.quantity} {e.sparePartName}</span>
                      <span className="spi-date">{formatDate(e.receivedAt)}</span>
                    </div>
                    <div className="spi-card-meta">
                      {e.receivedByName} {e.supplier && `• from ${e.supplier}`} • KES {e.totalCost}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ISSUE TO TECHNICIAN ── */}
          {tab === 'issue' && (
            <div>
              <div className="spi-form-card">
                <select className="spi-input" value={issueForm.partId}
                  onChange={e => setIssueForm({ ...issueForm, partId: e.target.value })}>
                  <option value="">Select part...</option>
                  {catalog.map(p => <option key={p.id} value={p.id}>{p.name} ({p.currentStock} in stock)</option>)}
                </select>
                <input className="spi-input" type="number" placeholder="Quantity to issue" value={issueForm.quantity}
                  onChange={e => setIssueForm({ ...issueForm, quantity: e.target.value })} />
                <select className="spi-input" value={issueForm.technicianId}
                  onChange={e => setIssueForm({ ...issueForm, technicianId: e.target.value })}>
                  <option value="">Select technician...</option>
                  {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <textarea className="spi-input" placeholder="Notes (optional)" value={issueForm.notes} rows={2}
                  onChange={e => setIssueForm({ ...issueForm, notes: e.target.value })} />
                <button className="spi-btn spi-btn-primary" onClick={handleIssue} disabled={busy}>
                  {busy ? 'Saving...' : '📤 Issue Part'}
                </button>
              </div>

              <div className="spi-list">
                {issues.slice(0, 15).map(i => (
                  <div key={i.id} className="spi-card">
                    <div className="spi-card-top">
                      <span className="spi-part-name">-{i.quantity} {i.sparePartName}</span>
                      <span className="spi-date">{formatDate(i.issuedAt)}</span>
                    </div>
                    <div className="spi-card-meta">
                      To {i.issuedToName} • by {i.issuedByName}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SparePartsInventory;
